import {
  AdjustmentItem,
  CycleCountAdjustmentType,
  CycleCountResearchMap,
  CycleCountVarianceReportItem,
} from '../models/cycle-count-session.model';

export function varianceItemKey(
  item: Pick<CycleCountVarianceReportItem, 'materialId' | 'subLocationCode'>
): string {
  return `${item.materialId}|${item.subLocationCode}`;
}

export function isAllVariancesResolved(
  varianceItems: CycleCountVarianceReportItem[],
  researchMap: CycleCountResearchMap
): boolean {
  return varianceItems
    .filter((item) => item.isVariance)
    .every((item) => {
      const state = researchMap[varianceItemKey(item)];
      return state?.status === 'Approved' || state?.status === 'Skip';
    });
}

export function buildApprovedResearchMapFromSelection(
  varianceItems: CycleCountVarianceReportItem[],
  selectedKeys: ReadonlySet<string>,
  notesByKey: Readonly<Record<string, string>> = {}
): CycleCountResearchMap {
  return varianceItems.reduce<CycleCountResearchMap>((map, item) => {
    const key = varianceItemKey(item);
    const approved = selectedKeys.has(key);
    map[key] = {
      status: approved ? 'Approved' : 'Skip',
      notes: approved ? (notesByKey[key] ?? '') : '',
    };
    return map;
  }, {});
}

/**
 * #29187 / #32048: the fail-closed submit gate for the required adjustment note. Returns the keys of
 * every Approved variance item that WAS ADJUSTED (the actual count was edited) but whose note is
 * blank/whitespace. #32048: a note is required only when an adjustment was made — a variance posted
 * as-counted (isAdjusted false) needs no note. An empty array means the batch is fully documented and
 * may be submitted; a non-empty array must block submit. Only Approved items are checked — Skip/Pending
 * items are excluded from the batch and never require a note.
 */
export function findApprovedItemsMissingNotes(
  varianceItems: CycleCountVarianceReportItem[],
  researchMap: CycleCountResearchMap
): string[] {
  return (
    varianceItems
      .filter((item) => item.isVariance)
      // #32048: only an adjusted row (the actual count was edited) requires a note; posting a variance
      // as-counted does not. Narrows the #29187 "note on every posted variance" gate per business direction.
      .filter((item) => item.isAdjusted)
      .map((item) => varianceItemKey(item))
      .filter((key) => {
        const state = researchMap[key];
        return state?.status === 'Approved' && !state.notes?.trim();
      })
  );
}

/**
 * #29187: the outcome of preparing a Viewpoint adjustment submission from the current selection and
 * notes. `missing-notes` and `empty` are the two block reasons the caller surfaces as messages;
 * `ok` carries the batch ready to POST.
 */
export type AdjustmentSubmissionResult =
  | { kind: 'missing-notes'; missingCount: number }
  | { kind: 'empty' }
  | { kind: 'ok'; batch: AdjustmentItem[] };

/**
 * #29187: one place that turns the checkbox selection + per-item notes into a submit decision —
 * builds the research map, enforces the required-note gate (fail-closed), then the non-empty gate,
 * and only then returns the batch. Keeps the component free of the ordering rules and makes them
 * unit-testable.
 */
export function prepareAdjustmentSubmission(
  varianceItems: CycleCountVarianceReportItem[],
  selectedKeys: ReadonlySet<string>,
  notesByKey: Readonly<Record<string, string>>
): AdjustmentSubmissionResult {
  const researchMap = buildApprovedResearchMapFromSelection(
    varianceItems,
    selectedKeys,
    notesByKey
  );
  const missingNotes = findApprovedItemsMissingNotes(
    varianceItems,
    researchMap
  );
  if (missingNotes.length > 0) {
    return { kind: 'missing-notes', missingCount: missingNotes.length };
  }
  const batch = buildAdjustmentBatch(varianceItems, researchMap);
  if (batch.length === 0) {
    return { kind: 'empty' };
  }
  return { kind: 'ok', batch };
}

/**
 * #29187: max length of the operator-entered INTR order number. The viewpoint-api order writer rejects
 * an OrderNumber longer than 10, and the live INTR corpus is all <= 10.
 */
export const MAX_INTR_ORDER_NUMBER_LENGTH = 10;

/**
 * #29187: split an approved adjustment batch into the shortage lines (which post to Viewpoint now, as an
 * MS-ticket batch) and the overage count (deferred — overage is a separate $0 path, out of scope). The
 * backend rejects a mixed payload, so the frontend posts shortages only.
 */
export function splitAdjustmentBatchForViewpoint(batch: AdjustmentItem[]): {
  shortages: AdjustmentItem[];
  overageCount: number;
} {
  const shortages = batch.filter((i) => i.adjustmentType === 'Shortage');
  return { shortages, overageCount: batch.length - shortages.length };
}

/**
 * #29187: a job is valid only when the WHOLE value is `<prefix>.NN` — a non-empty prefix, a dot, then
 * exactly a two-digit company at the end. Anchored to end-of-string so malformed jobs like `562608.267`
 * (three trailing digits) or `.26` (no prefix) are rejected up front. The backend
 * `BatchRepository.GetJobCompany` reads only the two chars after the last dot (lenient), so this is
 * deliberately no looser than the backend and stricter on garbage the backend would silently truncate.
 */
export function isValidJobNumber(
  jobNumber: string | null | undefined
): boolean {
  return /^.+\.\d{2}$/.test((jobNumber ?? '').trim());
}

/** #29187: field-level errors for the operator-entered post fields; empty object means valid. */
export interface ViewpointPostFieldErrors {
  orderNumber?: string;
  jobNumber?: string;
}

/**
 * #29187: validate the operator-entered INTR order number + job before posting to Viewpoint. Mirrors the
 * backend guards (INTR non-blank and <= 10 chars; job `xxx.NN`) so the operator gets an inline error
 * instead of a 400.
 */
export function validateViewpointPostFields(
  orderNumber: string,
  jobNumber: string
): ViewpointPostFieldErrors {
  const errors: ViewpointPostFieldErrors = {};
  const intr = (orderNumber ?? '').trim();
  if (!intr) {
    errors.orderNumber = 'An INTR order number is required.';
  } else if (intr.length > MAX_INTR_ORDER_NUMBER_LENGTH) {
    errors.orderNumber = `INTR order number must be ${MAX_INTR_ORDER_NUMBER_LENGTH} characters or fewer.`;
  }
  if (!isValidJobNumber(jobNumber)) {
    errors.jobNumber = 'Enter a valid job number (e.g. 562608.26).';
  }
  return errors;
}

export function buildAdjustmentBatch(
  varianceItems: CycleCountVarianceReportItem[],
  researchMap: CycleCountResearchMap
): AdjustmentItem[] {
  return varianceItems
    .filter((item) => item.isVariance)
    .filter((item) => researchMap[varianceItemKey(item)]?.status === 'Approved')
    .map((item) => {
      // Derive the adjustment from the report's authoritative `variance` — the same field the
      // shortage/overage tabs, totals, and classifyVariance use — NOT a raw actualQty - expectedQty.
      // For an ADJUSTED row applyCountAdjustmentToReportItem preserves the ORIGINAL actualQty and only
      // recomputes `variance`, so recomputing from actualQty mis-signs it and drops genuine shortages
      // from the Viewpoint batch (a shortage would read as a match/overage and be excluded).
      const adjustmentQty = item.isNotFound
        ? -item.expectedQty
        : (item.variance ?? 0);
      const adjustmentType: CycleCountAdjustmentType =
        adjustmentQty < 0 ? 'Shortage' : 'Overage';
      // Effective actual, kept consistent with the signed delta (actualQty - expectedQty === adjustmentQty).
      const actualQty = item.expectedQty + adjustmentQty;
      return {
        materialId: item.materialId,
        subLocationCode: item.subLocationCode,
        expectedQty: item.expectedQty,
        actualQty,
        adjustmentQty,
        adjustmentType,
        adjustmentNote: researchMap[varianceItemKey(item)].notes,
        // #32048: carry whether the count was edited so the API requires a note only for adjusted rows.
        isAdjusted: item.isAdjusted,
      };
    });
}
